import 'animate.css/animate.min.css'
import 'font-awesome/css/font-awesome.css'
import './styles.scss'
import './index.css'
import './overrides.scss'
import 'react-image-lightbox/style.css'
import * as Sentry from '@sentry/react'
import { createBrowserHistory } from 'history'
import React from 'react'
import { render } from 'react-dom'
import { Provider } from 'react-redux'
import { applyMiddleware, combineReducers, createStore } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import thunkMiddleware from 'redux-thunk'
import App from './App'
import { __isEnvDev } from './config'
import api from './middleware/api'
import { stripNavigationFragment, stripRequestUrlFragment } from './utils/sentry_hooks'
import lastUserTransactionVisited from './middleware/lastUserTransactionVisited'
import tokenRefreshMiddleware from './middleware/token_refresh'
import { auth, tx, txById, txState } from './reducers'
import { getAgentBatch, putAgentBatch } from './reducers/agent_batch'
import { accreditationGridSort } from './reducers/accreditation_grid_sort'
import { accreditationStatuses } from './reducers/accreditation_statuses'
import { businessUpdateTxState } from './reducers/business_update_tx_state'
import { manualAml } from './reducers/manual-aml'
import { processTransactionStartModalDialog } from './reducers/start_process_transaction'
import { appContext } from './reducers/app_context'
import { business } from './reducers/business'
import { businessFilter } from './reducers/business_filter'
import { businessDocumentTypes } from './reducers/business_document_types'
import { businessIdentities } from './reducers/business_identities'
import { businessInfoModalDialog } from './reducers/business_info_modal_dialog'
import { businessAddressModalDialog } from './reducers/business_address_modal_dialog'
import { businessSettings } from './reducers/business_settings'
import { businessTransactionSort } from './reducers/business_tx_table_sort'
import { notificationsSort } from './reducers/notifications_table_sort'
import { accessCodesSort } from './reducers/access_codes_table_sort'
import { businesses } from './reducers/businesses'
import { businessesAll } from './reducers/businesses_all'
import { countryFilter } from './reducers/country_filter'
import { csvExport } from './reducers/csv_export'
import { fetchHowToDocumentation, fetchClientSupportDocuments } from './reducers/how_to_documentation'
import { eSign } from './reducers/e_sign'
import { errorFilter } from './reducers/error_filter'
import { errorManagement } from './reducers/error_management'
import { featureFlag } from './reducers/feature_flag'
import { identityInfoModalDialog } from './reducers/identity_info_modal_dialog'
import { lightbox } from './reducers/lightbox'
import { allMetrics, metricsFilter } from './reducers/metrics'
import { modalConfirmation } from './reducers/modal_confirmation'
import { modalDialog } from './reducers/modal_dialog'
import { passwordReset } from './reducers/password_reset'
import { identityPdfModalDialog } from './reducers/pdf_modal_dialog'
import { srcLightbox } from './reducers/src_lightbox'
import { statusFilter } from './reducers/status_filter'
import { refreshAccessToken } from './reducers/token_refresh'
import { transactionCallback } from './reducers/transaction_callback'
import { transactionRestart } from './reducers/transaction_restart'
import { transactionSearch } from './reducers/transaction_search'
import { txGridSort } from './reducers/tx_grid_sort'
import { insightsSort } from './reducers/insights_sort'
import { updateBusinessSettings } from './reducers/update_business_settings'
import { updateProfile } from './reducers/update_profile'
import { createUser, getUser, getUsers, getUserById, updateUser, resendEmail, usersFilter } from './reducers/users'
import { verifyDocument } from './reducers/verify_document'
import { disableImage } from './reducers/disable_image'
import { updateDocumentData } from './reducers/update_document_data'
import { businessRestart } from './reducers/business_restart'

import {
  getNotifications,
  getNotification,
  makeReadNotification,
  getAllNotifications,
  getImportanceNotifications,
  makeUnreadNotification,
} from './reducers/notifications'
import { fetchRelatedTransactions } from './reducers/related_transactions'
import { searchPhoneNumberTransactions } from './reducers/search_phone_number'
import { searchDocumentNumberTransactions } from './reducers/search_document_number'
import { getAccessCodes } from './reducers/access_codes'
import { fetchTransactionsDeclineDefinitions } from './reducers/transactions_decline_definitions'
import {
  fetchSupportedDocuments,
  fetchClientSupportedDocuments,
  fetchGenericTypeDocuments,
} from './reducers/supported_documents'
import sessionCheckMiddleware from './middleware/session_check'
import * as serviceWorker from './serviceWorker'
import { connectRouter } from 'connected-react-router'
import { routerMiddleware } from 'connected-react-router'
import { ConnectedRouter } from 'connected-react-router'
import { selfieCheckFilter } from './reducers/selfie-check'

if (process.env.REACT_APP_SENTRY_URL) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_URL,
    release: `compliance-dashboard@${process.env.REACT_APP_VERSION}`,
    beforeBreadcrumb: stripNavigationFragment,
    beforeSend: stripRequestUrlFragment,
  })
}

const reducers = (history) =>
  combineReducers({
    auth,
    tx,
    txById,
    lightbox,
    srcLightbox,
    modalDialog,
    modalConfirmation,
    txState,
    businessUpdateTxState,
    refreshAccessToken,
    statusFilter,
    businesses,
    business,
    transactionSearch,
    transactionRestart,
    txGridSort,
    insightsSort,
    identityInfoModalDialog,
    businessInfoModalDialog,
    businessAddressModalDialog,
    identityPdfModalDialog,
    processTransactionStartModalDialog,
    businessIdentities,
    businessSettings,
    errorManagement,
    accreditationStatuses,
    accreditationGridSort,
    getAgentBatch,
    putAgentBatch,
    csvExport,
    passwordReset,
    eSign,
    appContext,
    verifyDocument,
    disableImage,
    updateDocumentData,
    featureFlag,
    manualAml,
    getUser,
    getUsers,
    getUserById,
    createUser,
    updateUser,
    resendEmail,
    updateProfile,
    usersFilter,
    updateBusinessSettings,
    transactionCallback,
    businessTransactionSort,
    notificationsSort,
    accessCodesSort,
    allMetrics,
    businessesAll,
    errorFilter,
    businessFilter,
    selfieCheckFilter,
    businessDocumentTypes,
    fetchHowToDocumentation,
    fetchClientSupportDocuments,
    countryFilter,
    getNotifications,
    getAllNotifications,
    getImportanceNotifications,
    getNotification,
    fetchRelatedTransactions,
    searchPhoneNumberTransactions,
    searchDocumentNumberTransactions,
    fetchSupportedDocuments,
    fetchClientSupportedDocuments,
    fetchGenericTypeDocuments,
    makeReadNotification,
    makeUnreadNotification,
    getAccessCodes,
    fetchTransactionsDeclineDefinitions,
    metricsFilter,
    businessRestart,
    router: connectRouter(history),
  })

export const history = createBrowserHistory()
const rootElement = document.getElementById('root')
let createStoreWithMiddleware = null

if (__isEnvDev) {
  createStoreWithMiddleware = composeWithDevTools(
    applyMiddleware(
      thunkMiddleware,
      api,
      tokenRefreshMiddleware,
      routerMiddleware(history),
      lastUserTransactionVisited,
      sessionCheckMiddleware
    )
  )(createStore)
} else {
  createStoreWithMiddleware = applyMiddleware(
    thunkMiddleware,
    api,
    tokenRefreshMiddleware,
    routerMiddleware(history),
    lastUserTransactionVisited,
    sessionCheckMiddleware
  )(createStore)
}

export const store = createStoreWithMiddleware(reducers(history))
render(
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <App history={history} />
    </ConnectedRouter>
  </Provider>,
  rootElement
)

serviceWorker.unregister()
